import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  RefundRequest, 
  RefundRequestCreateDto, 
  RefundRequestUpdateDto,
  RefundRequestResponse
} from "../../modules/shared/model/refund-request.interfaces";
import { 
  getRefundRequests as getRefundRequestsAction, 
  getRefundRequestsByUser as getRefundRequestsByUserAction,
  getRefundRequestById as getRefundRequestByIdAction,
  createRefundRequestAction,
  updateRefundRequestAction,
  deleteRefundRequestAction,
  bulkCreateRefundRequestsAction,
  bulkUpdateRefundRequestsAction,
  getRefundRequestsByContract as getRefundRequestsByContractAction
} from "../Actions/RefundRequestActions";

// Async Thunks
export const fetchRefundRequests = createAsyncThunk("refundRequests/fetchAll", async (query: Record<string, string | number | boolean | object> | undefined, { rejectWithValue }) => {
  try { 
    return await getRefundRequestsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch refund requests"); 
  }
});

export const fetchRefundRequestsByUser = createAsyncThunk("refundRequests/fetchByUser", async ({ userId, query }: { userId: string; query?: Record<string, string | number | boolean | object> }, { rejectWithValue }) => {
  try { 
    return await getRefundRequestsByUserAction(userId, query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch refund requests by user"); 
  }
});

export const fetchRefundRequestById = createAsyncThunk("refundRequests/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getRefundRequestByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch refund request"); 
  }
});

export const createRefundRequest = createAsyncThunk("refundRequests/create", async (data: RefundRequestCreateDto, { rejectWithValue }) => {
  try { 
    return await createRefundRequestAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create refund request"); 
  }
});

export const updateRefundRequest = createAsyncThunk("refundRequests/update", async ({ id, data }: { id: string; data: RefundRequestUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateRefundRequestAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update refund request"); 
  }
});

export const deleteRefundRequest = createAsyncThunk("refundRequests/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteRefundRequestAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete refund request"); 
  }
});

export const bulkCreateRefundRequests = createAsyncThunk("refundRequests/bulkCreate", async (refundRequests: RefundRequestCreateDto[], { rejectWithValue }) => {
  try { 
    return await bulkCreateRefundRequestsAction(refundRequests); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk create refund requests"); 
  }
});

export const bulkUpdateRefundRequests = createAsyncThunk("refundRequests/bulkUpdate", async (refundRequests: Array<{ id: string; data: RefundRequestUpdateDto }>, { rejectWithValue }) => {
  try { 
    return await bulkUpdateRefundRequestsAction(refundRequests); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk update refund requests"); 
  }
});

export const fetchRefundRequestsByContract = createAsyncThunk("refundRequests/fetchByContract", async ({ contractId, query }: { contractId: string; query?: Record<string, string | number | boolean | object> }, { rejectWithValue }) => {
  try { 
    return await getRefundRequestsByContractAction(contractId, query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch refund requests by contract"); 
  }
});

// State interface
interface RefundRequestState {
  refundRequests: RefundRequest[];
  currentRefundRequest: RefundRequest | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    count: number;
    totalCount: number;
    hasNextPage: boolean;
  };
}

// Initial state
const initialState: RefundRequestState = {
  refundRequests: [],
  currentRefundRequest: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    count: 0,
    totalCount: 0,
    hasNextPage: false,
  },
};

// Slice
const refundRequestSlice = createSlice({
  name: "refundRequests",
  initialState,
  reducers: {
    clearRefundRequestError: (state) => {
      state.error = null;
    },
    clearCurrentRefundRequest: (state) => {
      state.currentRefundRequest = null;
    },
    setRefundRequestLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    updateRefundRequestInList: (state, action: PayloadAction<RefundRequest>) => {
      const index = state.refundRequests.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.refundRequests[index] = action.payload;
      }
    },
    removeRefundRequestFromList: (state, action: PayloadAction<string>) => {
      state.refundRequests = state.refundRequests.filter(item => item.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all refund requests
      .addCase(fetchRefundRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRefundRequests.fulfilled, (state, action: PayloadAction<RefundRequestResponse>) => {
        state.loading = false;
        state.refundRequests = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          count: action.payload.count,
          totalCount: action.payload.totalCount,
          hasNextPage: action.payload.hasNextPage,
        };
      })
      .addCase(fetchRefundRequests.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch refund requests by user
      .addCase(fetchRefundRequestsByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRefundRequestsByUser.fulfilled, (state, action: PayloadAction<RefundRequestResponse>) => {
        state.loading = false;
        state.refundRequests = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          count: action.payload.count,
          totalCount: action.payload.totalCount,
          hasNextPage: action.payload.hasNextPage,
        };
      })
      .addCase(fetchRefundRequestsByUser.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch refund request by ID
      .addCase(fetchRefundRequestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRefundRequestById.fulfilled, (state, action: PayloadAction<RefundRequest>) => {
        state.loading = false;
        state.currentRefundRequest = action.payload;
      })
      .addCase(fetchRefundRequestById.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create refund request
      .addCase(createRefundRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRefundRequest.fulfilled, (state, action: PayloadAction<RefundRequest>) => {
        state.loading = false;
        state.refundRequests.unshift(action.payload);
        state.pagination.count += 1;
      })
      .addCase(createRefundRequest.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update refund request
      .addCase(updateRefundRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRefundRequest.fulfilled, (state, action: PayloadAction<RefundRequest>) => {
        state.loading = false;
        const index = state.refundRequests.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.refundRequests[index] = action.payload;
        }
        if (state.currentRefundRequest?.id === action.payload.id) {
          state.currentRefundRequest = action.payload;
        }
      })
      .addCase(updateRefundRequest.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete refund request
      .addCase(deleteRefundRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRefundRequest.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.refundRequests = state.refundRequests.filter(item => item.id !== action.payload);
        state.pagination.count -= 1;
        if (state.currentRefundRequest?.id === action.payload) {
          state.currentRefundRequest = null;
        }
      })
      .addCase(deleteRefundRequest.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk create refund requests
      .addCase(bulkCreateRefundRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkCreateRefundRequests.fulfilled, (state, action: PayloadAction<RefundRequest[]>) => {
        state.loading = false;
        state.refundRequests.unshift(...action.payload);
        state.pagination.count += action.payload.length;
      })
      .addCase(bulkCreateRefundRequests.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk update refund requests
      .addCase(bulkUpdateRefundRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateRefundRequests.fulfilled, (state, action: PayloadAction<RefundRequest[]>) => {
        state.loading = false;
        action.payload.forEach(updatedRefundRequest => {
          const index = state.refundRequests.findIndex(item => item.id === updatedRefundRequest.id);
          if (index !== -1) {
            state.refundRequests[index] = updatedRefundRequest;
          }
        });
      })
      .addCase(bulkUpdateRefundRequests.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch refund requests by contract
      .addCase(fetchRefundRequestsByContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRefundRequestsByContract.fulfilled, (state, action: PayloadAction<RefundRequestResponse>) => {
        state.loading = false;
        state.refundRequests = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          count: action.payload.count,
          totalCount: action.payload.totalCount,
          hasNextPage: action.payload.hasNextPage,
        };
      })
      .addCase(fetchRefundRequestsByContract.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearRefundRequestError,
  clearCurrentRefundRequest,
  setRefundRequestLoading,
  updateRefundRequestInList,
  removeRefundRequestFromList,
} = refundRequestSlice.actions;

export default refundRequestSlice.reducer;
